import { useState, useEffect } from "react";
import { Wallet, CheckCircle, XCircle, User, Settings } from "lucide-react";
import { clsx } from "clsx";
import { Modal } from "../../../components/ui/Modal";
import { Badge, statusVariant } from "../../../components/ui/Badge";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { ToastContainer } from "../../../components/ui/Toast";
import { useToast } from "../../../hooks/useToast";
import { useBizLoans, useBizApproveLoan, useBizRejectLoan, useBizReleaseLoan } from "../../../hooks/useBizAdmin";
import { useAuth } from "../../../hooks/useAuth";
import { useLoanConfig, useSaveLoanConfig } from "../../../hooks/useLoan";
import type { Loan, LoanStatus } from "../../../lib/loanApi";
import { formatCurrency, formatDate, formatPeriod } from "../../../utils/format";
import { ApiError } from "../../../lib/api";

// ── Helpers ──────────────────────────────────────────────────────────────────

function displayName(loan: Loan): string {
  return loan.employeeName ?? `Employee (${loan.userId.slice(0, 8)}…)`;
}

function termLabel(termPeriods: number): string {
  const months = termPeriods / 2;
  if (months === 1) return "1 month";
  if (months < 12) return `${months} months`;
  if (months === 12) return "1 year";
  return `${months / 12} years`;
}

function nextPeriodLabel(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  if (now.getDate() <= 15) {
    return `${monthNames[month]} ${year} (16–EOM)`;
  } else {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    return `${monthNames[nextMonth]} ${nextYear} (1–15)`;
  }
}

// ── Loan Detail Modal ─────────────────────────────────────────────────────────

interface DetailModalProps {
  loan: Loan;
  onClose: () => void;
  onApprove: (loanId: string, userId: string) => Promise<void>;
  onReject: (loanId: string, userId: string, reason: string) => Promise<void>;
  onRelease: (loanId: string, userId: string, releaseDate: string, disbursementAccount?: string) => Promise<void>;
  isSubmitting: boolean;
}

function LoanDetailModal({ loan, onClose, onApprove, onReject, onRelease, isSubmitting }: DetailModalProps) {
  const [rejectReason, setRejectReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [action, setAction] = useState<"APPROVE" | "REJECT" | null>(null);
  const [releaseDate, setReleaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [disbursementAccount, setDisbursementAccount] = useState("");

  const { canApproveAction } = useAuth();
  const canApprove = canApproveAction("loans", "approve");
  const canReject = canApproveAction("loans", "reject");
  const canRelease = canApproveAction("loans", "release");

  const { data: loanConfig } = useLoanConfig(loan.businessId ?? "");

  const isPending = loan.status === "PENDING";
  const isApproved = loan.status === "APPROVED";
  const isActive = loan.status === "ACTIVE";
  const isRejected = loan.status === "REJECTED";

  const installmentAmount = loan.installmentAmount ?? Math.round((loan.amount / loan.termPeriods) * 100) / 100;

  const handleApprove = async () => {
    setAction("APPROVE");
    await onApprove(loan.loanId, loan.userId);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setReasonError("Rejection reason is required.");
      return;
    }
    setReasonError(null);
    setAction("REJECT");
    await onReject(loan.loanId, loan.userId, rejectReason.trim());
  };

  const handleRelease = async () => {
    await onRelease(loan.loanId, loan.userId, releaseDate, disbursementAccount || undefined);
  };

  return (
    <Modal isOpen onClose={onClose} title="Loan Application Details" size="md">
      <div className="space-y-5">
        {/* Employee info */}
        <div className="flex items-center gap-3 rounded-xl bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#eff3ff]">
            <User className="h-5 w-5 text-[#2447d9]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#0f172a]">{displayName(loan)}</p>
            <p className="text-xs text-[#64748b]">Applied {formatDate(loan.createdAt)}</p>
          </div>
          <div className="ml-auto">
            <Badge variant={statusVariant(loan.status)}>{loan.status}</Badge>
          </div>
        </div>

        {/* Loan details grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
            <p className="text-xs text-[#94a3b8] mb-1">Loan Amount</p>
            <p className="text-sm font-bold text-[#0f172a]">{formatCurrency(loan.amount)}</p>
          </div>

          <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
            <p className="text-xs text-[#94a3b8] mb-1">Term</p>
            <p className="text-sm font-semibold text-[#0f172a]">{loan.termPeriods} periods</p>
            <p className="text-xs text-[#64748b]">{termLabel(loan.termPeriods)}</p>
          </div>

          <div className="col-span-2 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
            <p className="text-xs text-[#94a3b8] mb-1">Installment Preview</p>
            <p className="text-sm font-semibold text-[#0f172a]">
              {formatCurrency(installmentAmount)} / period × {loan.termPeriods} periods
            </p>
            {loan.interestRate !== undefined && (
              <p className="text-xs text-[#64748b] mt-0.5">Interest rate: {loan.interestRate}% p.a. (flat)</p>
            )}
          </div>

          {isPending && (
            <div className="col-span-2 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
              <p className="text-xs text-[#94a3b8] mb-1">First Deduction Estimate</p>
              <p className="text-sm font-semibold text-[#0f172a]">
                Starting {nextPeriodLabel()}
              </p>
            </div>
          )}

          <div className="col-span-2 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
            <p className="text-xs text-[#94a3b8] mb-1">Purpose / Reason</p>
            <p className="text-sm text-[#0f172a]">{loan.reason}</p>
          </div>

          {isRejected && loan.rejectedReason && (
            <div className="col-span-2 rounded-lg bg-[#fef2f2] border border-[#fca5a5] px-4 py-3">
              <p className="text-xs text-[#94a3b8] mb-1">Rejection Reason</p>
              <p className="text-sm text-[#dc2626]">{loan.rejectedReason}</p>
              {loan.approvedBy && (
                <p className="text-xs text-[#64748b] mt-1">Reviewed by {loan.approvedBy}{loan.reviewedAt ? ` · ${formatDate(loan.reviewedAt)}` : ""}</p>
              )}
            </div>
          )}

          {isApproved && (
            <div className="col-span-2 rounded-lg bg-[#fffbeb] border border-[#fde68a] px-4 py-3">
              <p className="text-sm font-medium text-[#92400e]">
                Loan approved — pending release of funds.
              </p>
              {loan.approvedBy && (
                <p className="text-xs text-[#64748b] mt-1">Approved by: {loan.approvedBy}{loan.reviewedAt ? ` · ${formatDate(loan.reviewedAt)}` : ""}</p>
              )}
            </div>
          )}

          {isActive && (
            <div className="col-span-2 rounded-lg bg-[#dcfce7] border border-[#86efac] px-4 py-3">
              <p className="text-sm font-medium text-[#15803d]">
                This loan is active and being deducted from payslip.
              </p>
              {loan.approvedBy && (
                <p className="text-xs text-[#64748b] mt-1">Released by: {loan.approvedBy ?? "—"}{loan.releaseDate ? ` · ${formatDate(loan.releaseDate)}` : ""}</p>
              )}
              {loan.firstInstallmentPeriod && (
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-[#374151]">
                  <div>
                    <p className="text-[#64748b]">Per period</p>
                    <p className="font-semibold">{formatCurrency(loan.installmentAmount ?? installmentAmount)}</p>
                  </div>
                  <div>
                    <p className="text-[#64748b]">First deduction</p>
                    <p className="font-semibold">{formatPeriod(loan.firstInstallmentPeriod)}</p>
                  </div>
                  <div>
                    <p className="text-[#64748b]">Last deduction</p>
                    <p className="font-semibold">{formatPeriod(loan.lastInstallmentPeriod!)}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Audit trail */}
        <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 space-y-2">
          <p className="text-xs font-semibold text-[#374151] uppercase tracking-wide">Audit Trail</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div>
              <p className="text-[#94a3b8]">Applied On</p>
              <p className="font-medium text-[#374151]">{formatDate(loan.createdAt)}</p>
            </div>
            <div>
              <p className="text-[#94a3b8]">Last Updated</p>
              <p className="font-medium text-[#374151]">{loan.updatedAt ? formatDate(loan.updatedAt) : "—"}</p>
            </div>
            {loan.approvedBy && (
              <div>
                <p className="text-[#94a3b8]">Reviewed By</p>
                <p className="font-medium text-[#374151]">{loan.approvedBy}</p>
              </div>
            )}
            {loan.reviewedAt && (
              <div>
                <p className="text-[#94a3b8]">Reviewed On</p>
                <p className="font-medium text-[#374151]">{formatDate(loan.reviewedAt)}</p>
              </div>
            )}
            {loan.releaseDate && (
              <div>
                <p className="text-[#94a3b8]">Released On</p>
                <p className="font-medium text-[#374151]">{formatDate(loan.releaseDate)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Approve / Reject section — only for pending */}
        {isPending && (
          <div className="border-t border-[#e2e8f0] pt-4 space-y-3">
            <p className="text-sm font-medium text-[#0f172a]">Review this loan application</p>

            <div>
              <label className="block text-xs font-medium text-[#64748b] mb-1.5">
                Rejection reason{" "}
                <span className="text-[#94a3b8]">(required if rejecting)</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => {
                  setRejectReason(e.target.value);
                  if (reasonError) setReasonError(null);
                }}
                rows={3}
                maxLength={500}
                placeholder="Provide a reason if rejecting this loan…"
                className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
              />
              {reasonError && <p className="text-xs text-[#dc2626] mt-1">{reasonError}</p>}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                disabled={isSubmitting || !canApprove}
                title={!canApprove ? "You don't have permission to approve" : undefined}
                onClick={() => void handleApprove()}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#16a34a] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#15803d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircle className="h-4 w-4" />
                {isSubmitting && action === "APPROVE" ? "Approving…" : "Approve"}
              </button>
              <button
                type="button"
                disabled={isSubmitting || !canReject}
                title={!canReject ? "You don't have permission to reject" : undefined}
                onClick={() => void handleReject()}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-[#dc2626] px-4 py-2.5 text-sm font-semibold text-[#dc2626] hover:bg-[#fef2f2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircle className="h-4 w-4" />
                {isSubmitting && action === "REJECT" ? "Rejecting…" : "Reject"}
              </button>
            </div>
            {!canApprove && !canReject && (
              <p className="text-xs text-[#94a3b8]">You don't have permission to review loan applications.</p>
            )}
          </div>
        )}

        {/* Release section — only for approved loans */}
        {isApproved && (
          <div className="border-t border-[#e2e8f0] pt-4 space-y-3">
            <p className="text-sm font-medium text-[#0f172a]">Release this loan</p>
            <div>
              <label className="block text-xs font-medium text-[#64748b] mb-1.5">Release Date</label>
              <input
                type="date"
                value={releaseDate}
                onChange={(e) => setReleaseDate(e.target.value)}
                className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
              />
              <p className="text-xs text-[#64748b] mt-1">Installments begin the period after this date.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#64748b] mb-1.5">
                Disbursement Account <span className="text-[#94a3b8]">(optional)</span>
              </label>
              {(loanConfig?.disbursementAccounts?.length ?? 0) > 0 ? (
                <select
                  value={disbursementAccount}
                  onChange={(e) => setDisbursementAccount(e.target.value)}
                  className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
                >
                  <option value="">— Select account (optional) —</option>
                  {loanConfig!.disbursementAccounts!.map((a) => (
                    <option key={a.id} value={a.name}>{a.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={disbursementAccount}
                  onChange={(e) => setDisbursementAccount(e.target.value)}
                  placeholder="e.g. BDO Bank, GCash…"
                  className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
                />
              )}
            </div>
            <button
              onClick={() => void handleRelease()}
              disabled={isSubmitting || !releaseDate || !canRelease}
              title={!canRelease ? "You don't have permission to release" : undefined}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#16a34a] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#15803d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Release Loan
            </button>
            {!canRelease && (
              <p className="text-xs text-[#94a3b8]">You don't have permission to release loans.</p>
            )}
          </div>
        )}

        {/* Close button for non-pending / non-approved loans */}
        {!isPending && !isApproved && (
          <div className="flex justify-end border-t border-[#e2e8f0] pt-4">
            <button
              onClick={onClose}
              className="rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Loan Config Modal ─────────────────────────────────────────────────────────

interface LoanConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
}

const PRESET_TERMS = [2, 4, 6, 12, 18, 24, 36, 48];

function LoanConfigModal({ isOpen, onClose, businessId }: LoanConfigModalProps) {
  const { data: config, isLoading } = useLoanConfig(businessId);
  const saveConfig = useSaveLoanConfig(businessId);

  const [interestRate, setInterestRate] = useState(0);
  const [selectedTerms, setSelectedTerms] = useState<number[]>([6, 12, 24]);
  const [maxAmount, setMaxAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [newAccountName, setNewAccountName] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    if (config) {
      setInterestRate(config.interestRate);
      setSelectedTerms(config.allowedTerms);
      setMaxAmount(config.maxLoanableAmount ? String(config.maxLoanableAmount) : "");
      setAccounts(config.disbursementAccounts?.map((a: { id: string; name: string }) => a.name) ?? []);
    }
  }, [config, isOpen]);

  const toggleTerm = (t: number) => {
    setSelectedTerms((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t].sort((a, b) => a - b),
    );
  };

  // Preview table: installment per ₱10,000 for each selected term
  const previewRows = selectedTerms.map((t) => {
    const termYears = t / 24;
    const totalInterest = 10000 * (interestRate / 100) * termYears;
    const installment = Math.round(((10000 + totalInterest) / t) * 100) / 100;
    const months = t / 2;
    return {
      t,
      label: months < 12 ? `${months}mo` : `${months / 12}yr`,
      installment,
    };
  });

  const handleSave = async () => {
    if (selectedTerms.length === 0) return;
    setSaving(true);
    try {
      await saveConfig.mutateAsync({
        interestRate,
        allowedTerms: selectedTerms,
        maxLoanableAmount: maxAmount ? parseFloat(maxAmount) : undefined,
        disbursementAccounts: accounts.map((name, i) => ({ id: String(i + 1), name })),
      });
      onClose();
    } catch {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Loan Configuration" size="md">
      {isLoading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : (
        <div className="space-y-5">
          {/* Interest rate */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
              Annual Interest Rate (flat)
            </label>
            <div className="relative w-40">
              <input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={interestRate}
                onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 pr-8 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">%</span>
            </div>
            <p className="mt-1 text-xs text-[#94a3b8]">0% means no interest (principal only).</p>
          </div>

          {/* Allowed terms */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-2">
              Allowed Repayment Terms (semi-monthly periods)
            </label>
            <div className="flex flex-wrap gap-2">
              {PRESET_TERMS.map((t) => {
                const months = t / 2;
                const lbl = months < 12 ? `${months}mo` : `${months / 12}yr`;
                const active = selectedTerms.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTerm(t)}
                    className={clsx(
                      "rounded-lg px-3 py-1.5 text-xs font-semibold border transition-colors",
                      active
                        ? "border-[#2447d9] bg-[#eff3ff] text-[#2447d9]"
                        : "border-[#e2e8f0] bg-white text-[#64748b] hover:bg-[#f8fafc]",
                    )}
                  >
                    {t}p / {lbl}
                  </button>
                );
              })}
            </div>
            {selectedTerms.length === 0 && (
              <p className="mt-1 text-xs text-[#dc2626]">Select at least one term.</p>
            )}
          </div>

          {/* Max loanable amount */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
              Maximum Loanable Amount
            </label>
            <div className="relative w-48">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
              <input
                type="number"
                min={0}
                step={1000}
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                placeholder="No limit"
                className="w-full rounded-lg border border-[#e2e8f0] bg-white pl-7 pr-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
              />
            </div>
            <p className="mt-1 text-xs text-[#94a3b8]">Leave blank for no limit.</p>
          </div>

          {/* Disbursement accounts */}
          <div>
            <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
              Disbursement Accounts
            </label>
            <p className="text-xs text-[#94a3b8] mb-2">
              Configure payment channels available when releasing loans (e.g. BDO Bank, GCash, Cash on Hand).
            </p>
            {accounts.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {accounts.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#eff3ff] border border-[#c7d2fe] px-3 py-1 text-xs font-medium text-[#2447d9]"
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => setAccounts((prev) => prev.filter((a) => a !== name))}
                      className="text-[#6366f1] hover:text-[#dc2626] transition-colors leading-none"
                      aria-label={`Remove ${name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const trimmed = newAccountName.trim();
                    if (trimmed && !accounts.includes(trimmed)) {
                      setAccounts((prev) => [...prev, trimmed]);
                    }
                    setNewAccountName("");
                  }
                }}
                placeholder="e.g. BDO Bank"
                className="flex-1 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
              />
              <button
                type="button"
                onClick={() => {
                  const trimmed = newAccountName.trim();
                  if (trimmed && !accounts.includes(trimmed)) {
                    setAccounts((prev) => [...prev, trimmed]);
                  }
                  setNewAccountName("");
                }}
                className="rounded-lg border border-[#2447d9] bg-[#eff3ff] px-3 py-2 text-sm font-medium text-[#2447d9] hover:bg-[#dbeafe] transition-colors"
              >
                Add
              </button>
            </div>
          </div>

          {/* Preview table */}
          {previewRows.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-2">
                Installment preview (per ₱10,000 principal)
              </p>
              <div className="rounded-lg border border-[#e2e8f0] overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                      <th className="px-3 py-2 text-left text-[#64748b]">Term</th>
                      <th className="px-3 py-2 text-left text-[#64748b]">Periods</th>
                      <th className="px-3 py-2 text-right text-[#64748b]">Per period</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {previewRows.map((row) => (
                      <tr key={row.t} className="bg-white">
                        <td className="px-3 py-2 text-[#0f172a]">{row.label}</td>
                        <td className="px-3 py-2 text-[#64748b]">{row.t}</td>
                        <td className="px-3 py-2 text-right font-semibold text-[#2447d9]">
                          {formatCurrency(row.installment)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2 border-t border-[#e2e8f0]">
            <button type="button" onClick={onClose}
              className="flex-1 rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors">
              Cancel
            </button>
            <button type="button" onClick={() => void handleSave()} disabled={saving || selectedTerms.length === 0}
              className="flex-1 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              {saving ? "Saving…" : "Save Configuration"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type TabStatus = LoanStatus | "ALL";

const TABS: { label: string; value: TabStatus }[] = [
  { label: "All",      value: "ALL"      },
  { label: "Pending",  value: "PENDING"  },
  { label: "Approved", value: "APPROVED" },
  { label: "Active",   value: "ACTIVE"   },
  { label: "Rejected", value: "REJECTED" },
];

export default function AdminLoansPage() {
  const [activeTab, setActiveTab] = useState<TabStatus>("PENDING");
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const { toasts, toast, dismissToast } = useToast();

  // Track last-seen counts per status (updated as each tab loads) so all tab
  // badges stay informative even when a different tab is selected.
  const [knownCounts, setKnownCounts] = useState<Partial<Record<TabStatus, number>>>({});

  const { activeBusinessId } = useAuth();
  const bizId = activeBusinessId ?? "";

  const statusFilter = activeTab === "ALL" ? undefined : activeTab as LoanStatus;
  const bizLoans = useBizLoans(bizId, statusFilter);
  const bizApprove = useBizApproveLoan(bizId);
  const bizReject = useBizRejectLoan(bizId);
  const bizRelease = useBizReleaseLoan(bizId);

  const isSubmitting = bizApprove.isPending || bizReject.isPending || bizRelease.isPending;
  const isLoading = bizLoans.isLoading;
  const items: Loan[] = bizLoans.data ?? [];

  // Update the count badge for the current tab whenever data loads
  useEffect(() => {
    if (!isLoading && bizLoans.data !== undefined) {
      setKnownCounts((prev) => ({ ...prev, [activeTab]: bizLoans.data!.length }));
    }
  }, [isLoading, bizLoans.data, activeTab]);

  const handleApprove = async (loanId: string, userId: string) => {
    try {
      await bizApprove.mutateAsync({ loanId, userId });
      toast.success("Loan approved successfully.");
      setSelectedLoan(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === "ALREADY_REVIEWED") {
        toast.error("This loan has already been reviewed.");
        setSelectedLoan(null);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  const handleReject = async (loanId: string, userId: string, reason: string) => {
    try {
      await bizReject.mutateAsync({ loanId, userId, reason });
      toast.success("Loan rejected.");
      setSelectedLoan(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === "ALREADY_REVIEWED") {
        toast.error("This loan has already been reviewed.");
        setSelectedLoan(null);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  const handleRelease = async (loanId: string, userId: string, releaseDate: string, disbursementAccount?: string) => {
    try {
      await bizRelease.mutateAsync({ loanId, userId, releaseDate, disbursementAccount });
      toast.success("Loan released successfully.");
      setSelectedLoan(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === "ALREADY_REVIEWED") {
        toast.error("This loan has already been processed.");
        setSelectedLoan(null);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <div className="space-y-5">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* ── Header card ───────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
              <Wallet className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0f172a]">Manage Loans</h1>
              <p className="text-xs text-[#64748b]">Review and process employee loan applications</p>
            </div>
          </div>
          {bizId && (
            <button
              onClick={() => setShowConfig(true)}
              title="Loan Configuration"
              className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-1.5 text-xs font-medium text-[#64748b] hover:border-[#2447d9] hover:text-[#2447d9] hover:bg-[#eff3ff] transition-colors"
            >
              <Settings className="h-3.5 w-3.5" />
              Configure
            </button>
          )}
        </div>
      </div>

      {!bizId ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#e2e8f0] bg-white py-16 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#eff3ff]">
            <Wallet className="h-6 w-6 text-[#2447d9]" />
          </div>
          <p className="text-sm text-[#64748b]">Select a business to manage loan applications.</p>
        </div>
      ) : (
        <>
          {/* ── Tabs ────────────────────────────────────────────────────── */}
          <div className="flex gap-1 rounded-lg bg-[#f1f5f9] p-1 w-fit overflow-x-auto overflow-y-hidden">
            {TABS.map((tab) => {
              const count = knownCounts[tab.value];
              return (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={clsx(
                    "flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap",
                    activeTab === tab.value
                      ? "bg-white text-[#0f172a] shadow-sm"
                      : "text-[#64748b] hover:text-[#0f172a]",
                  )}
                >
                  {tab.label}
                  {count !== undefined && count > 0 && (
                    <span className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${
                      activeTab === tab.value
                        ? "bg-[#eff3ff] text-[#2447d9]"
                        : "bg-[#e2e8f0] text-[#64748b]"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Table ───────────────────────────────────────────────────── */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner size="lg" className="text-[#2447d9]" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#e2e8f0] bg-white py-16 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#eff3ff]">
                <Wallet className="h-6 w-6 text-[#2447d9]" />
              </div>
              <p className="text-sm font-medium text-[#0f172a]">No loan applications</p>
              <p className="text-xs text-[#94a3b8]">
                {activeTab === "ALL"
                  ? "No loan applications yet."
                  : `No ${activeTab.toLowerCase()} loans for this business.`}
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-[#e2e8f0] bg-white overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[600px]">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                      {["Employee", "Amount", "Term", "Date", "Status"].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {items.map((loan) => (
                      <tr
                        key={loan.loanId}
                        onClick={() => setSelectedLoan(loan)}
                        className="bg-white hover:bg-[#f8fafc] cursor-pointer transition-colors group"
                      >
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#eff3ff] text-xs font-bold text-[#2447d9]">
                              {(loan.employeeName ?? "?")[0]?.toUpperCase()}
                            </div>
                            <p className="text-sm font-medium text-[#0f172a] group-hover:text-[#2447d9] transition-colors">
                              {displayName(loan)}
                            </p>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-sm font-bold text-[#0f172a]">{formatCurrency(loan.amount)}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-sm text-[#0f172a]">{loan.termPeriods} periods</p>
                          <p className="text-xs text-[#94a3b8]">{termLabel(loan.termPeriods)}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-sm text-[#64748b]">{formatDate(loan.createdAt)}</p>
                          {loan.releaseDate && (
                            <p className="text-xs text-[#16a34a] mt-0.5">
                              Released {formatDate(loan.releaseDate)}
                              {loan.approvedBy ? ` by ${loan.approvedBy}` : ""}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <Badge variant={statusVariant(loan.status)}>{loan.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {selectedLoan && (
            <LoanDetailModal
              loan={selectedLoan}
              onClose={() => setSelectedLoan(null)}
              onApprove={handleApprove}
              onReject={handleReject}
              onRelease={async (loanId, userId, releaseDate, disbursementAccount) => {
                await handleRelease(loanId, userId, releaseDate, disbursementAccount);
              }}
              isSubmitting={isSubmitting}
            />
          )}
        </>
      )}

      {showConfig && bizId && (
        <LoanConfigModal
          isOpen={showConfig}
          onClose={() => setShowConfig(false)}
          businessId={bizId}
        />
      )}
    </div>
  );
}
